import { showSnackbarMessage } from '@derstandard/utils';
import { getIdToken, userIsLoggedIn } from './authentication';
import { trackUserAction } from '$lib/tracking';
import languageStrings from '$i18n/de';

/**
 * Wrapper function for all requests
 * @param {String} method
 * @param {String} url
 * @param {RequestInit} fetchOptions
 * @param {Boolean} promptLogin - whether to prompt (with login overlay) the user to sign in
 * @return {*}
 */
async function _fetch(pathName: string, fetchOptions?: RequestInit) {
	if (!(await userIsLoggedIn())) {
		return;
	}

	const apiUrl = VITE_READINGLIST_API_URL;

	const options = {
		...fetchOptions,
		headers: {
			Authorization: `Bearer ${await getIdToken()}`,
			'Content-Type': 'application/json',
			'X-Requested-From': window.document.location.href,
		},
	};

	const response = await fetch(`${apiUrl}${pathName}`, options);

	return response;
}

/**
 * Parses response and return JSON
 * @param response
 * @returns {Promise<ListItem[]>}
 * @throws {Error} if response is not ok
 */
async function parseResponse(response: Response): Promise<ListItem[]> {
	if (typeof response === 'undefined') {
		return;
	}

	if (response.ok) {
		return await response.json();
	} else {
		throw new Error(response.statusText);
	}
}

function handleResponse(response: Response, customString?: string): Promise<ListItem[]> {
	if (!response.ok) {
		showSnackbarMessage(customString || languageStrings.store.error.default);
	}

	return parseResponse(response);
}

export async function getItems(): Promise<ListItem[]> {
	const response = await _fetch(`/getItems`);
	return parseResponse(response);
}

export async function addItem(item: ListItem): Promise<ListItem[]> {
	const response = await _fetch(`/addItem`, {
		body: JSON.stringify(item),
		method: 'POST',
	});

	if (!response.ok) {
		/**
 		 * Handle addItem response separately for Smart metering. Server will respond with HTTP 402 if user is not whitelisted.
 		 * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/402}
 		 */
		if (response.status === 402) {
			showSnackbarMessage(languageStrings.store.error.notWhitelisted);
			trackUserAction('readlater_add_prohibited');
			return;
		} else {
			showSnackbarMessage(languageStrings.store.error.add)
			return;
		}
	}

	return parseResponse(response);
}

export async function removeItem(item: ListItem): Promise<ListItem[]> {
	const response = await _fetch(`/removeItem`, {
		body: JSON.stringify(item),
		method: 'DELETE',
	});

	return handleResponse(response, languageStrings.store.error.remove);
}

export async function removeItems(items: ListItem[]): Promise<ListItem[]> {
	const response = await _fetch(`/removeItems`, {
		body: JSON.stringify({ items: items }),
		method: 'DELETE',
	});

	return handleResponse(response, languageStrings.store.error.removeVisited);
}

export async function markVisited(item: ListItem): Promise<ListItem[]> {
	const response = await _fetch(`/markItemVisited`, {
		body: JSON.stringify(item),
		method: 'PUT',
	});

	return handleResponse(response);
}

export async function removeAllItems(): Promise<ListItem[]> {
	// Request body cannot be empty due to content type set to "application/json"
	const response = await _fetch(`/removeAllItems`, { body: '{}', method: 'DELETE' });

	return handleResponse(response, languageStrings.store.error.clear);
}
